import type {
  DailyChallenge,
  GhostFrame,
  HighlightMoment,
  RunSummary,
  ScoreRow,
  ThemeId,
} from '../shared/types';

export interface ConfigResponse {
  version: string;
  rules: number;
  daily: DailyChallenge;
  themes: ThemeId[];
}

export interface SubmitResult {
  id: number;
  board: string;
  rank: number;
  best: boolean;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* body was not JSON */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const api = {
  config: () => req<ConfigResponse>('/api/config'),
  daily: () => req<DailyChallenge>('/api/daily'),

  scores: (board: string, opts: { limit?: number; order?: 'score' | 'time' } = {}) => {
    const q = new URLSearchParams({ board });
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.order) q.set('order', opts.order);
    return req<{ board: string; rows: ScoreRow[] }>(`/api/scores?${q}`);
  },

  submit: (body: {
    name: string;
    playerId?: string;
    run: RunSummary;
    ghost?: GhostFrame[];
    highlights?: HighlightMoment[];
  }) => req<SubmitResult>('/api/scores', { method: 'POST', body: JSON.stringify(body) }),

  ghost: (id: number) => req<{ frames: GhostFrame[] }>(`/api/ghost/${id}`),

  narrate: (runId: number) =>
    req<{ text: string }>('/api/narrate', { method: 'POST', body: JSON.stringify({ runId }) }),

  newRace: () => req<{ code: string }>('/api/race', { method: 'POST', body: '{}' }),
};

/** Local identity. No accounts — a uuid in localStorage is enough for two brothers. */
const ID_KEY = 'gnarwhal.playerId';
const NAME_KEY = 'gnarwhal.name';

export function playerId(): string {
  let v = localStorage.getItem(ID_KEY);
  if (!v) {
    v = crypto.randomUUID ? crypto.randomUUID() : `p-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem(ID_KEY, v);
  }
  return v;
}

export function riderName(): string {
  return localStorage.getItem(NAME_KEY) ?? '';
}

export function setRiderName(name: string): void {
  localStorage.setItem(NAME_KEY, name.slice(0, 14));
}
